import { COLUMN_TO_FIELD_MAP } from '../TransportCloudTable.consts';

interface CellUpdatePayload {
  itemId: string;
  columnKey: string;
  newValue: any;
  oldValue?: any;
}

interface BackendCellUpdate {
  rowId?: number;
  shipmentPlanContainerId?: number;
  fieldName: string;
  newValue: any;
}

type RecordIdPart = { rowId: number } | { shipmentPlanContainerId: number };

type BackendCellUpdateResult = BackendCellUpdate | BackendCellUpdate[];

const DATE_COLUMNS = ['pickupDate', 'loadingDeliveryDate', 'deliveryDate'];
const SELECT_COLUMNS = [
  'shippingLine',
  'containerType',
  'loadingDeliveryLocation',
  'pickupLocation',
  'deliveryLocation',
  'trucker',
  'productGroup',
  'containerStatus',
  'customer',
];
const CUSTOMS_CLEARANCE_COLUMN = 'customsClearance';
const CURRENCY_COLUMN = 'truckerRate';

const transformDateValue = (value: any): number | null => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.getTime();
};

const transformSelectValue = (value: any): number | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'number') return value;

  if (typeof value === 'object') {
    if ('id' in value && value.id !== null && value.id !== undefined) {
      return typeof value.id === 'number' ? value.id : parseInt(value.id, 10);
    }
  }

  return null;
};

const transformCustomsClearanceValue = (value: any, _rowData?: any): any => {
  if (!value) {
    return {
      status: null,
      placeId: null,
    };
  }

  if (typeof value === 'object' && 'status' in value) {
    return {
      status: value.status || null,
      placeId: value.placeId || null,
    };
  }

  let status: string | null = null;
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();

    if (
      lowerValue.includes('done') ||
      lowerValue.includes('odpraw') ||
      lowerValue.includes('zakład')
    ) {
      status = 'DONE';
    } else if (
      lowerValue.includes('progress') ||
      lowerValue.includes('tranzyt') ||
      lowerValue.includes('port')
    ) {
      status = 'IN_PROGRESS';
    } else if (lowerValue.includes('none') || lowerValue.includes('brak')) {
      status = 'NONE';
    }
  }

  return {
    status,
    placeId: null,
  };
};

const transformCurrencyValue = (value: any, rowData?: any): any => {
  if (!value) {
    return {
      amount: null,
      currencyId: null,
    };
  }

  if (typeof value === 'object') {
    const amount = value.rate || value.amount || null;
    const currencyId = value.currency?.id || value.currencyId || null;

    if (!amount && !currencyId) {
      return {
        amount: null,
        currencyId: null,
      };
    }

    return {
      amount: amount ? parseFloat(amount) : null,
      currencyId: currencyId
        ? typeof currencyId === 'number'
          ? currencyId
          : parseInt(currencyId, 10)
        : null,
    };
  }

  if (typeof value === 'string') {
    if (value.trim() === '') {
      return {
        amount: null,
        currencyId: null,
      };
    }

    const parts = value.trim().split(' ');
    const amount = parts[0] ? parseFloat(parts[0]) : null;

    const currencyId = rowData?.truckerRateCurrencyId || null;

    return {
      amount: amount || null,
      currencyId,
    };
  }

  if (typeof value === 'number') {
    const currencyId = rowData?.truckerRateCurrencyId || null;

    return {
      amount: value,
      currencyId,
    };
  }

  return {
    amount: null,
    currencyId: null,
  };
};

const transformReferenceNumbers = (
  value: any,
  idPart: RecordIdPart,
): BackendCellUpdate => {
  let number1 = null;
  let number2 = null;
  let number3 = null;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    const parts = trimmedValue.split(/[/,]/).map((p) => p.trim());

    if (parts[0]) {
      const firstPart = parts[0];
      number1 = firstPart && firstPart !== '-' ? firstPart : null;
    }

    if (parts[1]) {
      number2 = parts[1] && parts[1] !== '-' ? parts[1] : null;
    }

    if (parts[2]) {
      number3 = parts[2] && parts[2] !== '-' ? parts[2] : null;
    }
  }

  return {
    ...idPart,
    fieldName: 'referenceNumbers',
    newValue: {
      job: number1,
      loading: number2,
      delivery: number3,
    },
  };
};

export const transformCellUpdateForBackend = (
  payload: CellUpdatePayload,
  rowData?: any,
): BackendCellUpdateResult => {
  const { itemId, columnKey, newValue } = payload;

  const rowIdMatch = itemId.match(/^job-(\d+)$/);
  const planIdMatch = itemId.match(/^plan-(\d+)$/);

  if (!rowIdMatch && !planIdMatch) {
    throw new Error(
      `Cannot update cell: itemId ${itemId} does not have a valid jobEntryId or shipmentPlanContainerId`,
    );
  }

  const idPart: RecordIdPart = rowIdMatch
    ? { rowId: parseInt(rowIdMatch[1], 10) }
    : { shipmentPlanContainerId: parseInt(planIdMatch![1], 10) };

  if (columnKey === 'referenceNumbers') {
    return transformReferenceNumbers(newValue, idPart);
  }

  const fieldName = COLUMN_TO_FIELD_MAP[columnKey] || columnKey;

  let transformedValue: any = newValue;

  if (DATE_COLUMNS.includes(columnKey)) {
    transformedValue = transformDateValue(newValue);
  } else if (SELECT_COLUMNS.includes(columnKey)) {
    transformedValue = transformSelectValue(newValue);
  } else if (columnKey === CUSTOMS_CLEARANCE_COLUMN) {
    transformedValue = transformCustomsClearanceValue(newValue, rowData);
  } else if (columnKey === CURRENCY_COLUMN) {
    transformedValue = transformCurrencyValue(newValue, rowData);
  } else if (newValue === '') {
    transformedValue = null;
  }

  return {
    ...idPart,
    fieldName,
    newValue: transformedValue,
  };
};
